<template>
  <div class="MyMapLeftSideBar">
    <!-- Narrow icon navigation bar -->
    <nav
      class="MyMapLeftSideBar__iconBar"
      :aria-label="$t('myMap.leftSidebar.navLabel', 'Navigation')"
    >
      <Teleport to="body">
        <span
          v-if="tooltipVisible"
          class="MyMapLeftSideBar__tooltip"
          :style="tooltipStyle"
        >{{ tooltipLabel }}</span>
      </Teleport>

      <button
        v-if="!userStore.userIsLogged"
        type="button"
        class="MyMapLeftSideBar__iconBtn"
        :class="{ 'MyMapLeftSideBar__iconBtn--active': activeSection === 'welcome' }"
        :aria-label="$t('myMap.leftSidebar.welcome.title')"
        @click="toggleSection('welcome')"
        @mouseenter="showTooltip($event, $t('myMap.leftSidebar.welcome.title'))"
        @mouseleave="hideTooltip"
      >
        <Icon icon="material-symbols-light:home-outline-rounded" :size="24" />
      </button>

      <button
        v-if="showDomainesIcon"
        type="button"
        class="MyMapLeftSideBar__iconBtn"
        :class="{ 'MyMapLeftSideBar__iconBtn--active': activeSection === 'domaines' }"
        :aria-label="$t('myMap.domaines.title', 'Domaines')"
        @click="toggleSection('domaines')"
        @mouseenter="showTooltip($event, $t('myMap.domaines.title', 'Domaines'))"
        @mouseleave="hideTooltip"
      >
        <Icon icon="material-symbols-light:category-rounded" :size="24" />
      </button>

      <button
        v-if="showPlatformAccordions"
        type="button"
        class="MyMapLeftSideBar__iconBtn"
        :class="{ 'MyMapLeftSideBar__iconBtn--active': activeSection === 'ressources' }"
        :aria-label="$t('myMap.leftSidebar.platformCategories.resource', 'Ressources')"
        @click="toggleSection('ressources')"
        @mouseenter="showTooltip($event, $t('myMap.leftSidebar.platformCategories.resource', 'Ressources'))"
        @mouseleave="hideTooltip"
      >
        <Icon icon="material-symbols-light:inventory-2-outline" :size="24" />
      </button>

      <button
        v-if="showPlatformAccordions"
        type="button"
        class="MyMapLeftSideBar__iconBtn"
        :class="{ 'MyMapLeftSideBar__iconBtn--active': activeSection === 'projets' }"
        :aria-label="$t('myMap.leftSidebar.platformCategories.project', 'Projets')"
        @click="toggleSection('projets')"
        @mouseenter="showTooltip($event, $t('myMap.leftSidebar.platformCategories.project', 'Projets'))"
        @mouseleave="hideTooltip"
      >
        <Icon icon="material-symbols-light:engineering" :size="24" />
      </button>

      <button
        v-if="showProfilIcon"
        type="button"
        class="MyMapLeftSideBar__iconBtn"
        :class="{ 'MyMapLeftSideBar__iconBtn--active': activeSection === 'profil' }"
        :aria-label="$t('myMap.leftSidebar.platformCategories.profile', 'Profil')"
        @click="toggleSection('profil')"
        @mouseenter="showTooltip($event, $t('myMap.leftSidebar.platformCategories.profile', 'Profil'))"
        @mouseleave="hideTooltip"
      >
        <Icon icon="material-symbols-light:map-outline-rounded" :size="24" />
      </button>

      <button
        v-if="atlasesList.length > 0"
        type="button"
        class="MyMapLeftSideBar__iconBtn"
        :class="{ 'MyMapLeftSideBar__iconBtn--active': activeSection === 'atlases' }"
        :aria-label="$t('atlas.predefinedMap', 'Cartes prédéfinies')"
        @click="toggleSection('atlases')"
        @mouseenter="showTooltip($event, $t('atlas.predefinedMap', 'Cartes prédéfinies'))"
        @mouseleave="hideTooltip"
      >
        <Icon icon="material-symbols-light:map-outline-rounded" :size="24" />
      </button>

      <button
        v-if="visibleFondLayerStyles.length > 0"
        type="button"
        class="MyMapLeftSideBar__iconBtn"
        :class="{ 'MyMapLeftSideBar__iconBtn--active': activeSection === 'carte' }"
        :aria-label="$t('myMap.sections.carte', 'Carte')"
        @click="toggleSection('carte')"
        @mouseenter="showTooltip($event, $t('myMap.sections.carte', 'Carte'))"
        @mouseleave="hideTooltip"
      >
        <Icon icon="material-symbols-light:layers-outline" :size="24" />
      </button>
    </nav>

    <!-- Content panel -->
    <Transition name="MyMapLeftSideBar__panelTransition">
      <div v-if="activeSection !== null" class="MyMapLeftSideBar__contentPanel">
        <div class="MyMapLeftSideBar__panelHeader">
          <span class="MyMapLeftSideBar__panelTitle">{{ activeSectionTitle }}</span>
          <button
            type="button"
            class="MyMapLeftSideBar__panelClose"
            :aria-label="$t('common.close', 'Fermer')"
            @click="closeActiveSection"
          >
            <Icon icon="material-symbols-light:close-rounded" :size="18" />
          </button>
        </div>

        <!-- Welcome -->
        <template v-if="activeSection === 'welcome'">
          <MyMapWelcomeBlock />
        </template>

        <!-- Domaines -->
        <template v-else-if="activeSection === 'domaines'">
          <MyMapDomaine
            v-for="domaine in visibleDomaines"
            :key="domaine.domaineId"
            :domaine="domaine"
            :highlight-orange="domaine.domaineId >= 10"
          />
        </template>

        <!-- Ressources -->
        <template v-else-if="activeSection === 'ressources'">
          <MyMapLayerPicker
            v-if="myMapStore.resourceLayer"
            v-model:main-layer="myMapStore.resourceLayer"
            v-model:sub-layers="myMapStore.resourceSubLayers"
            primary-parent-border
            @update="() => refreshLayer(ItemType.RESOURCE)"
          />
          <MyMapResourceFilters />
        </template>

        <!-- Projets -->
        <template v-else-if="activeSection === 'projets'">
          <MyMapLayerPicker
            v-if="myMapStore.projectLayer"
            v-model:main-layer="myMapStore.projectLayer"
            v-model:sub-layers="myMapStore.projectSubLayers"
            primary-parent-border
            @update="() => refreshLayer(ItemType.PROJECT)"
          />
          <MyMapProjectFilters />
        </template>

        <!-- Profil / Commune -->
        <template v-else-if="activeSection === 'profil'">
          <div class="MyMapLeftSideBar__communeSearch">
            <CommuneAutocomplete
              v-model="myMapStore.mapSearch"
              :disabled="isCommuneSelectionLocked"
              :placeholder="$t('myMap.leftSidebar.platformCategories.profileCommuneSearchPlaceholder', 'Rechercher une commune...')"
              :no-data-text="$t('myMap.leftSidebar.platformCategories.profileCommuneNoData', 'Aucune commune trouvée')"
              @commune-selected="onProfileCommuneSelected"
            />
          </div>
          <div v-if="myMapStore.communeFicheForMapProfile" class="MyMapLeftSideBar__communeCard">
            <div class="MyMapLeftSideBar__communeCardHeader">
              <div class="MyMapLeftSideBar__communeCardTitleBar" role="presentation">
                <span class="MyMapLeftSideBar__communeCardTitle">
                  {{ $t('myMap.leftSidebar.platformCategories.profileCommuneTitle', 'Fiche communale') }}
                </span>
              </div>
              <button
                v-if="!isCommuneSelectionLocked"
                type="button"
                class="MyMapLeftSideBar__clearButton"
                :aria-label="$t('myMap.leftSidebar.platformCategories.clearCommune', 'Effacer la sélection')"
                @click="myMapStore.clearCommuneFicheAndDeselect()"
              >
                <Icon icon="mdi:close" color="dark-grey" :size="18" />
              </button>
            </div>
            <CommuneCard :commune-data="myMapStore.communeFicheForMapProfile" />
          </div>
          <p v-else class="MyMapLeftSideBar__profileHint">
            {{ $t('myMap.leftSidebar.platformCategories.profileCommuneHint', 'Sélectionnez une commune sur la carte ou activez la couche Communes pour afficher la fiche.') }}
          </p>
        </template>

        <!-- Atlases -->
        <template v-else-if="activeSection === 'atlases'">
          <MyMapAtlasesList
            :title="$t('atlas.predefinedMap')"
            :type="AtlasGroup.PREDEFINED_MAP"
            :atlases="atlasesList"
          />
        </template>

        <!-- Carte (fond layer styles) -->
        <template v-else-if="activeSection === 'carte'">
          <MyMapFondLayerCard
            v-for="entry in visibleFondLayerStyles"
            :key="entry.id"
            :style-id="entry.id"
            :metadata="entry.metadata"
          />
        </template>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { AtlasGroup } from '@/models/enums/geo/AtlasGroup'
import { ItemType } from '@/models/enums/app/ItemType'
import { useAtlasStore } from '@/stores/atlasStore'
import { useDomaineStore } from '@/stores/domaineStore'
import { useSousDomaineStore } from '@/stores/sousDomaineStore'
import { useCompetenceStore } from '@/stores/competenceStore'
import { useUserStore } from '@/stores/userStore'
import { useMyMapStore } from '@/stores/myMapStore'
import { AppLayersService } from '@/services/map/AppLayersService'
import { CommuneLayerService } from '@/services/map/CommuneLayerService'
import Icon from '@/components/ui/Icon.vue'
import MyMapAtlasesList from '@/views/map/components/Atlases/MyMapAtlasesList.vue'
import MyMapDomaine from '@/views/map/components/Domaines/MyMapDomaine.vue'
import MyMapWelcomeBlock from '@/views/map/components/MyMapWelcomeBlock.vue'
import MyMapFondLayerCard from '@/views/map/components/FondLayer/MyMapFondLayerCard.vue'
import MyMapLayerPicker from '@/views/map/components/MyMapLayerPicker.vue'
import MyMapResourceFilters from '@/views/map/components/MyMapResourceFilters.vue'
import MyMapProjectFilters from '@/views/map/components/MyMapProjectFilters.vue'
import CommuneAutocomplete from '@/views/map/components/CommuneAutocomplete.vue'
import CommuneCard from '@/views/map/components/CommuneCard.vue'
import type { FondLayerVisibleIn } from '@/models/interfaces/map/FondLayerStyle'
import type { CommuneApiResponse } from '@/services/commune/CommuneService'
import { mapApiResponseToCommuneData } from '@/services/commune/CommuneService'
import { computed, nextTick, onMounted, ref, watch, type CSSProperties } from 'vue'
import { useI18n } from 'vue-i18n'

type LeftSection = 'welcome' | 'domaines' | 'ressources' | 'projets' | 'profil' | 'atlases' | 'carte'

const atlasStore = useAtlasStore()
const domaineStore = useDomaineStore()
const sousDomaineStore = useSousDomaineStore()
const competenceStore = useCompetenceStore()
const userStore = useUserStore()
const myMapStore = useMyMapStore()
const { t } = useI18n()
const isCommuneSelectionLocked = computed(() => myMapStore.lockedCommuneName != null)

const activeSection = ref<LeftSection | null>(null)

const TOOLTIP_GAP = 8
const tooltipVisible = ref(false)
const tooltipLabel = ref('')
const tooltipStyle = ref<CSSProperties>({})

function showTooltip(event: MouseEvent, label: string): void {
  const button = event.currentTarget as HTMLElement
  const rect = button.getBoundingClientRect()
  tooltipLabel.value = label
  tooltipStyle.value = {
    position: 'fixed',
    top: `${rect.top + rect.height / 2}px`,
    left: `${rect.right + TOOLTIP_GAP}px`,
    transform: 'translateY(-50%)',
  }
  tooltipVisible.value = true
}

function hideTooltip(): void {
  tooltipVisible.value = false
}

/** Domaines with sous-domaines (domaineId 7-9); domains outside this range have no sous-domaines and are hidden. */
const DOMAINE_ID_MIN_WITH_SOUS_DOMAINES = 7
const DOMAINE_ID_MAX_WITH_SOUS_DOMAINES = 9

const visibleDomaines = computed(() =>
  domaineStore.domaineList.filter(
    (domaine) =>
      domaine.domaineId >= DOMAINE_ID_MIN_WITH_SOUS_DOMAINES &&
      domaine.domaineId <= DOMAINE_ID_MAX_WITH_SOUS_DOMAINES,
  ),
)

const showDomainesIcon = computed(
  () =>
    userStore.userIsLogged &&
    visibleDomaines.value.length > 0,
)

const showPlatformAccordions = computed(
  () =>
    myMapStore.myMap != null ||
    myMapStore.resourceLayer != null ||
    myMapStore.projectLayer != null,
)

const showProfilIcon = computed(
  () => showPlatformAccordions.value || myMapStore.mapSearch != null,
)

function isVisibleInCurrentContext(visibleIn: FondLayerVisibleIn): boolean {
  const atlasActive = myMapStore.activeAtlas.leftPanel.active
  const hasCommune = myMapStore.mapSearch != null
  if (visibleIn === 'always') return !atlasActive
  if (visibleIn === 'commune_only') return !atlasActive && hasCommune
  if (visibleIn === 'atlas_only') return atlasActive
  return false
}

const FOND_STYLE_ID_COMMUNE = 'commune'

const visibleFondLayerStyles = computed(() => {
  const hasCommune = myMapStore.mapSearch != null
  return myMapStore.fondLayerStyles.filter(
    (s) =>
      isVisibleInCurrentContext(s.metadata.visible_in) &&
      !(hasCommune && s.id === FOND_STYLE_ID_COMMUNE) &&
      !s.metadata.atlas_category,
  )
})

const atlasesList = computed(() =>
  atlasStore.atlasList
    .filter((atlas) => atlas.atlasGroup === AtlasGroup.PREDEFINED_MAP)
    .sort((a, b) => a.position - b.position),
)

const activeSectionTitle = computed((): string => {
  switch (activeSection.value) {
    case 'welcome':
      return t('myMap.leftSidebar.welcome.title')
    case 'domaines':
      return t('myMap.domaines.title', 'Domaines')
    case 'ressources':
      return t('myMap.leftSidebar.platformCategories.resource', 'Ressources')
    case 'projets':
      return t('myMap.leftSidebar.platformCategories.project', 'Projets')
    case 'profil':
      return t('myMap.leftSidebar.platformCategories.profile', 'Profil')
    case 'atlases':
      return t('atlas.predefinedMap', 'Cartes prédéfinies')
    case 'carte':
      return t('myMap.sections.carte', 'Carte')
    default:
      return ''
  }
})

function toggleSection(section: LeftSection): void {
  const previous = activeSection.value
  const next = activeSection.value === section ? null : section

  if (
    previous === 'domaines' &&
    next !== 'domaines' &&
    isCommuneSelectionLocked.value
  ) {
    deactivateAllActiveIndicators()
  }

  activeSection.value = next
}

function closeActiveSection(): void {
  if (activeSection.value === 'domaines' && isCommuneSelectionLocked.value) {
    deactivateAllActiveIndicators()
  }
  activeSection.value = null
}

function deactivateAllActiveIndicators(): void {
  const activeIds = myMapStore.visibleIndicatorEntries.map((entry) => entry.id)
  for (const atgId of activeIds) {
    myMapStore.removeVisibleIndicator(atgId)
  }
}

watch(activeSection, async (newSection, oldSection) => {
  const myMap = myMapStore.myMap
  const hasLockedCommune = isCommuneSelectionLocked.value
  if (newSection === 'profil') {
    // In communal mode, display fiche from selected commune without forcing commune layer load.
    if (hasLockedCommune) {
      return
    }
    await nextTick()
    if (myMap) {
      const mapInstance = (myMap as { map?: { getSource: (id: string) => unknown } }).map
      if (!mapInstance?.getSource?.(CommuneLayerService.getSourceId())) {
        await CommuneLayerService.addCommuneLayerToMap(myMap)
      }
      CommuneLayerService.setCommuneLayerVisibility(myMap, true)
    }
  } else if (oldSection === 'profil') {
    // Keep communal context intact when leaving profile in communal mode.
    if (hasLockedCommune) {
      return
    }
    if (myMap) {
      CommuneLayerService.setCommuneLayerVisibility(myMap, false)
    }
    myMapStore.clearCommuneFicheAndDeselect()
  }
})

watch(
  () => myMapStore.communeFicheForMapProfile,
  (data) => {
    if (data) {
      activeSection.value = 'profil'
    }
  },
)

function onProfileCommuneSelected(commune: CommuneApiResponse): void {
  if (isCommuneSelectionLocked.value) return
  void myMapStore.zoomToCommuneBounds(
    commune.cmnTitre ?? '',
    commune.cmnLong,
    commune.cmnLat,
  )
  myMapStore.applyCommuneViewFromApiGeometry(commune)
  myMapStore.setCommuneFicheFromCommuneLayer(mapApiResponseToCommuneData(commune))
  if (commune.cmnCode) {
    myMapStore.selectCommuneFeatureByCode(commune.cmnCode)
  }
}

function isMainLayerShown(itemType: ItemType): boolean {
  switch (itemType) {
    case ItemType.PROJECT:
      return myMapStore.projectLayer?.isShown ?? false
    case ItemType.RESOURCE:
      return myMapStore.resourceLayer?.isShown ?? false
    default:
      return false
  }
}

async function refreshLayer(itemType: ItemType): Promise<void> {
  const myMap = myMapStore.myMap
  if (!myMap) return
  const mapInstance = (myMap as { map?: { getSource: (id: string) => unknown } }).map
  const sourceExists = mapInstance?.getSource?.(itemType)
  if (!sourceExists && isMainLayerShown(itemType)) {
    await AppLayersService.setPlatformDataLayer(itemType)
  }
  myMap.setData(itemType, AppLayersService.getGeojsonForMapDisplay(itemType))
}

const loadDomaines = async () => {
  if (userStore.userIsLogged) {
    await Promise.all([
      domaineStore.getAll(),
      sousDomaineStore.getAll(),
      competenceStore.getAll(),
    ])
  }
}

onMounted(async () => {
  await loadDomaines()
  await myMapStore.fetchFondLayerStyles()
})

watch(
  () => userStore.userIsLogged,
  async (isLogged) => {
    if (isLogged) {
      await loadDomaines()
    }
  },
)
</script>

<style lang="scss">
.MyMapLeftSideBar {
  display: flex;
  flex-flow: row nowrap;
  flex: 0 0 auto;
  background: rgb(var(--v-theme-surface));
  overflow: hidden;
}

.MyMapLeftSideBar__iconBar {
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: 3.5rem;
  flex: 0 0 3.5rem;
  padding: 0.5rem 0;
  gap: 0.125rem;
  background: rgb(var(--v-theme-surface));
  border-right: 1px solid rgba(var(--v-theme-main-grey), 0.2);
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100vh - var(--mymap-header-h));
}

.MyMapLeftSideBar__iconBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  cursor: pointer;
  color: rgb(var(--v-theme-main-grey));
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: rgba(var(--v-theme-main-blue), 0.08);
    color: rgb(var(--v-theme-main-blue));
  }

  &--active {
    background-color: rgba(var(--v-theme-main-blue), 0.12);
    color: rgb(var(--v-theme-main-blue));
  }
}


.MyMapLeftSideBar__contentPanel {
  /* CSS custom properties consumed by child accordion components */
  --sidebar-accordion-trigger-py: 0.5rem;
  --sidebar-accordion-row-padding-bottom: 0.5rem;
  --sidebar-accordion-panel-padding: 0.25rem 0 0.5rem 0;
  --sidebar-accordion-separator: 1px solid rgba(var(--v-theme-main-grey), 0.35);
  --sidebar-accordion-stack-gap: 0;

  display: flex;
  flex-flow: column nowrap;
  width: 24rem;
  flex: 0 0 24rem;
  padding: var(--panel-padding-y) var(--panel-padding-x);
  gap: var(--panel-section-gap);
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100vh - var(--mymap-header-h));
  border-right: 1px solid rgba(var(--v-theme-main-grey), 0.2);
}

.MyMapLeftSideBar__panelHeader {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(var(--v-theme-main-grey), 0.35);
  flex-shrink: 0;
}

.MyMapLeftSideBar__panelTitle {
  font-size: var(--panel-section-title-size);
  font-weight: var(--panel-section-title-weight);
  letter-spacing: var(--panel-section-title-tracking);
  text-transform: uppercase;
  color: rgb(var(--v-theme-main-blue));
}

.MyMapLeftSideBar__panelClose {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: var(--dim-radius, 4px);
  cursor: pointer;
  color: rgb(var(--v-theme-main-grey));
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-main-blue));
    outline-offset: 2px;
  }
}

.MyMapLeftSideBar__tooltip {
  position: fixed;
  white-space: nowrap;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1;
  color: #fff;
  background: rgba(30, 30, 30, 0.92);
  backdrop-filter: blur(6px);
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  pointer-events: none;
  z-index: 99999;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -4px;
    transform: translateY(-50%);
    border: 4px solid transparent;
    border-right-color: rgba(30, 30, 30, 0.92);
  }
}

.MyMapLeftSideBar__panelTransition-enter-active,
.MyMapLeftSideBar__panelTransition-leave-active {
  transition: opacity 0.15s ease;
}

.MyMapLeftSideBar__panelTransition-enter-from,
.MyMapLeftSideBar__panelTransition-leave-to {
  opacity: 0;
}

/* Commune / profile section */
.MyMapLeftSideBar__communeSearch {
  margin-bottom: 0.75rem;
}

.MyMapLeftSideBar__communeCard {
  margin-bottom: 0.75rem;
}

.MyMapLeftSideBar__communeCardHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.MyMapLeftSideBar__communeCardTitleBar {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  padding: 0.3rem 0.5rem;
  margin: 0.125rem 0 0.25rem 0;
  border-radius: 0.5rem;
  background: rgba(var(--v-theme-main-blue), 0.06);
}

.MyMapLeftSideBar__communeCardTitle {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-main-blue), 0.7);
  line-height: 1;
}

.MyMapLeftSideBar__clearButton {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: var(--dim-radius, 4px);
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-main-blue));
    outline-offset: 2px;
  }
}

.MyMapLeftSideBar__communeCard :deep(.CommuneCard) {
  box-shadow: none;
  padding: 1rem;
}

.MyMapLeftSideBar__profileHint {
  margin: 0 0 0.75rem 0;
  font-size: var(--panel-label-size, 0.8125rem);
  color: var(--panel-label-color, rgba(0, 0, 0, 0.6));
  line-height: 1.4;
}
</style>
